/**
 * Shared HTTP client for module API objects (`api`, `authApi`, `landingApi`).
 * Always sends cookies. JSON-encodes plain bodies. Parses `{ message }` on errors.
 */

export const API_BASE =
  import.meta.env.VITE_API_URL !== undefined
    ? import.meta.env.VITE_API_URL
    : "http://localhost:8787";

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export interface ApiRequestOptions {
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  headers?: HeadersInit;
}

/** fetch init plus query/JSON body helpers. `body` is encoded by apiRequest. */
export type ApiRequestInit = Omit<RequestInit, "body"> & ApiRequestOptions;

function withQuery(
  path: string,
  query?: Record<string, string | number | undefined>,
): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let message = fallback;
  let code: string | undefined;
  try {
    const body = (await res.json()) as { message?: string; code?: string };
    if (body.message) message = body.message;
    if (body.code) code = body.code;
  } catch {
    if (res.statusText) message = `${fallback}: ${res.statusText}`;
  }
  return new ApiError(message, res.status, code);
}

async function parseJson<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/** Low-level fetch with shared defaults. Callers that need 401-as-null use this. */
export async function apiRequest(
  path: string,
  init: ApiRequestInit = {},
): Promise<Response> {
  const { query, body, headers, ...rest } = init;
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const headerBag = new Headers(headers);

  let payload: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    if (isForm) {
      payload = body;
    } else if (typeof body === "string" || body instanceof Blob) {
      payload = body;
    } else {
      if (!headerBag.has("Content-Type")) {
        headerBag.set("Content-Type", "application/json");
      }
      payload = JSON.stringify(body);
    }
  }

  return fetch(`${API_BASE}${withQuery(path, query)}`, {
    credentials: "include",
    ...rest,
    headers: headerBag,
    body: payload,
  });
}

async function send<T>(
  method: string,
  path: string,
  options: ApiRequestOptions & { method?: string } = {},
): Promise<T> {
  const res = await apiRequest(path, { method, ...options });
  if (!res.ok) {
    throw await toApiError(res, `${method} ${path} failed`);
  }
  return parseJson<T>(res);
}

export const apiClient = {
  get<T>(path: string, options?: ApiRequestOptions): Promise<T> {
    return send<T>("GET", path, options);
  },
  post<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
    return send<T>("POST", path, { ...options, body });
  },
  patch<T>(
    path: string,
    body?: unknown,
    options?: ApiRequestOptions,
  ): Promise<T> {
    return send<T>("PATCH", path, { ...options, body });
  },
  delete<T>(path: string, options?: ApiRequestOptions): Promise<T> {
    return send<T>("DELETE", path, options);
  },
};
